import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { Loader2 } from 'lucide-react'
import * as emoji from 'node-emoji'
import { UploadItem } from '@wellesley/uploads'
import {
  cn,
  Tabs,
  TabsContent,
  TabsControls,
  UploadFileProps,
} from '@wellesley/ui'
import addEmojisToText from 'helpers/addEmojisToText'
import { MarkdownToolbar } from '../MarkdownToolbar'
import { MarkdownHtmlPreview } from '../MarkdownHtmlPreview'
import { resolveUploadPreviewUrls } from '../utils/resolveUploadPreviewUrls'
import {
  PlainMarkdownEditor,
  PlainMarkdownEditorProps,
} from '../PlainMarkdownEditor'
import { handleToolbarShortcutsKeyDown } from './handlers'
import { useAwaitedUploadPreviewUrls } from './useAwaitedUploadPreviewUrls'
import { emojisSlice } from '../../emojis/emojisSlice'
import { useMarkdownApiPreview } from '../utils/useMarkdownApiPreview'
import { createMarkdownActions } from '../MarkdownEditorState/actions'
import { Markdown } from '../Markdown'

export const outlinedPanelBaseClasses =
  'rounded-m bg-white outline outline-1 outline-input ring-offset-white transition-[outline] duration-200 ease-in-out'

type PreviewSource = 'api' | 'local'

export type MarkdownEditorProps = Pick<
  PlainMarkdownEditorProps,
  | 'id'
  | 'name'
  | 'disabled'
  | 'placeholder'
  | 'className'
  | 'value'
  | 'onChange'
  | 'onBlur'
  | 'onKeyDown'
  | 'focusTrigger'
> & {
  previewSource?: PreviewSource
  onAddMedia?: (files: UploadFileProps[]) => void
  uploads?: UploadItem[]
  footerActions?: React.ReactNode
}

export const MarkdownEditor = forwardRef<
  HTMLTextAreaElement,
  MarkdownEditorProps
>(
  (
    {
      previewSource = 'api',
      onAddMedia,
      uploads,
      className,
      footerActions,
      ...plainMarkdownEditorProps
    },
    ref,
  ) => {
    const isApiPreview = previewSource === 'api'
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement)

    const { value, onChange } = plainMarkdownEditorProps
    const actions = useMemo(
      () => createMarkdownActions(textareaRef, value, onChange),
      [value, onChange],
    )

    const [activeTab, setActiveTab] = useState<string>('markdown')

    const emojis = useSelector(emojisSlice.selectors.selectEmojis)
    const { uploadIdToPreviewUrlMap } = useAwaitedUploadPreviewUrls(uploads)

    const markdown = useMemo(() => {
      const mdWithResolvedEmojis = emoji.emojify(addEmojisToText(value, emojis))

      return isApiPreview
        ? mdWithResolvedEmojis
        : resolveUploadPreviewUrls(
            mdWithResolvedEmojis,
            uploadIdToPreviewUrlMap,
          )
    }, [value, uploadIdToPreviewUrlMap, emojis])

    const {
      htmlString,
      isLoading: isHtmlLoading,
      isError,
    } = useMarkdownApiPreview(markdown, isApiPreview)

    return (
      <div
        className={cn(
          outlinedPanelBaseClasses,
          'flex shrink-0 grow flex-col',
          'focus-within:outline-2 focus-within:outline-blue-600',
          'hover:outline-2 hover:outline-gray-400 focus-within:hover:outline-blue-600',
          className,
        )}
      >
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div
            role="toolbar"
            className={cn(
              'p-xxs flex items-center justify-between border-b bg-white',
            )}
          >
            <MarkdownToolbar
              className={cn(activeTab === 'visual' && 'invisible')}
              actions={actions}
              onAddMedia={onAddMedia}
              attachments={uploads}
              editorRef={textareaRef}
            />
            <div>
              <TabsControls
                className={cn(
                  'mt-none rounded-m gap-xxxs !p-xxxs bg-gray-100 after:!hidden',
                  '[&_button]:p-xxxs [&_button]:h-xs [&_button]:w-[80px] [&_button]:rounded-sm [&_button]:text-sm',
                  '[&_button_span]:p-xxxs [&_button[data-state=active]]:bg-white [&_button]:after:hidden',
                )}
                items={[
                  {
                    value: 'markdown',
                    label: 'Markdown',
                  },
                  {
                    value: 'visual',
                    label: 'Visual',
                  },
                ]}
              />
            </div>
          </div>
          <TabsContent value="markdown">
            <PlainMarkdownEditor
              ref={textareaRef}
              actions={actions}
              onKeyDown={(e) => {
                handleToolbarShortcutsKeyDown(e, actions)
              }}
              className="py-xs px-s min-h-64"
              {...plainMarkdownEditorProps}
            />
          </TabsContent>
          <TabsContent
            value="visual"
            className="focus-visible:outline-none focus-visible:ring-0"
          >
            <div className="py-xs px-s relative h-64 overflow-auto">
              {isApiPreview ? (
                <>
                  {isHtmlLoading ? (
                    <div className="flex h-full items-center justify-center text-gray-500">
                      <Loader2 size={24} className="animate-spin" />
                    </div>
                  ) : isError ? (
                    <span className="flex h-full items-center justify-center text-base text-gray-500">
                      Failed to load preview
                    </span>
                  ) : (
                    <MarkdownHtmlPreview
                      className="h-full"
                      htmlString={htmlString}
                    />
                  )}
                </>
              ) : (
                <Markdown className="h-full" markdown={markdown} />
              )}
            </div>
          </TabsContent>
        </Tabs>
        {footerActions && (
          <div className="gap-s p-s flex items-center justify-end">
            {footerActions}
          </div>
        )}
      </div>
    )
  },
)

MarkdownEditor.displayName = 'MarkdownEditor'
